import React from "react";
import {
  Users,
  MessageCircle,
  Award,
  RefreshCw,
  Search,
  PenTool,
  Rocket,
  LineChart,
  Instagram,
  Facebook,
} from "lucide-react";
import PageHero from "../components/PageHero";
import { Section, SectionHeading, Check, Eyebrow } from "../components/SectionPrimitives";
import CTABand from "../components/CTABand";

const segmentation = [
  "Região e cidade",
  "Idade e gênero",
  "Interesses específicos",
  "Comportamento de compra",
  "Públicos personalizados",
  "Remarketing por engajamento",
];

const benefits = [
  { icon: Users, title: "Mais Alcance", desc: "Mostre sua empresa para milhares de pessoas diariamente.", color: "#4285F4" },
  { icon: MessageCircle, title: "Mais Contatos", desc: "Receba mensagens diretamente no WhatsApp ou Direct.", color: "#EA4335" },
  { icon: Award, title: "Fortalecimento de Marca", desc: "Aumente sua autoridade e presença no seu mercado.", color: "#FBBC05" },
  { icon: RefreshCw, title: "Remarketing", desc: "Recupere potenciais clientes que ainda não compraram.", color: "#34A853" },
];

const services = [
  "Campanhas para WhatsApp (mensagens diretas)",
  "Facebook Ads",
  "Instagram Ads",
  "Geração de leads qualificados",
  "Remarketing inteligente",
  "Campanhas de vendas (catálogo e site)",
  "Tráfego para sites e landing pages",
];

const steps = [
  { icon: Search, label: "Diagnóstico", desc: "Analisamos seu mercado, concorrência e público ideal.", color: "#4285F4" },
  { icon: PenTool, label: "Planejamento", desc: "Criamos a estratégia ideal de campanhas e criativos.", color: "#EA4335" },
  { icon: Rocket, label: "Implementação", desc: "Configuramos as campanhas profissionalmente e ativamos.", color: "#FBBC05" },
  { icon: LineChart, label: "Otimização", desc: "Melhoramos os resultados continuamente com base nos dados.", color: "#34A853" },
];

export default function MetaAds() {
  return (
    <div data-testid="page-meta-ads">
      <PageHero
        breadcrumb="Meta Ads"
        eyebrow="Facebook & Instagram"
        title="Anúncios que geram"
        highlight="mais vendas."
        subtitle="Alcance milhares de potenciais clientes diariamente através de campanhas estratégicas no Facebook e Instagram."
      />

      {/* O que é Meta Ads */}
      <Section testId="meta-what-is">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Eyebrow>O que é Meta Ads</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#000000] mt-4 leading-[1.05]">
              A plataforma oficial de anúncios do Facebook e Instagram.
            </h2>
            <p className="mt-6 text-[#000000]/70 leading-relaxed">
              Permite segmentar campanhas com uma precisão única — atingindo
              exatamente o perfil de cliente ideal para o seu negócio.
            </p>
            <div className="flex gap-3 mt-8">
              <span className="inline-flex items-center gap-2 border border-gray-200 px-4 py-2 text-sm text-[#000000]">
                <Facebook size={16} className="text-[#4285F4]" />
                Facebook
              </span>
              <span className="inline-flex items-center gap-2 border border-gray-200 px-4 py-2 text-sm text-[#000000]">
                <Instagram size={16} className="text-[#EA4335]" />
                Instagram
              </span>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="eyebrow mb-5">Segmentação avançada</p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {segmentation.map((s, i) => (
                <Check key={s} color={["#4285F4", "#EA4335", "#FBBC05", "#34A853"][i % 4]}>
                  {s}
                </Check>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Benefícios */}
      <Section testId="meta-benefits" className="bg-[#F8F9FA] border-y border-gray-100">
        <SectionHeading
          eyebrow="Benefícios"
          title="Por que investir em Meta Ads."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.title} className="og-card bg-white" data-testid={`meta-benefit-${b.title}`}>
                <Icon size={26} style={{ color: b.color }} />
                <h3 className="font-display text-lg font-bold text-[#000000] mt-5">
                  {b.title}
                </h3>
                <p className="mt-3 text-sm text-[#000000]/70 leading-relaxed">
                  {b.desc}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Serviços Inclusos */}
      <Section testId="meta-services">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow color="#EA4335">Serviços inclusos</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#000000] mt-4 leading-[1.05]">
              Um ecossistema completo de campanhas pagas nas redes sociais.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-5 border border-gray-100 p-8">
              {services.map((s, i) => (
                <Check key={s} color={["#4285F4", "#EA4335", "#FBBC05", "#34A853"][i % 4]}>
                  {s}
                </Check>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Como Trabalhamos */}
      <Section testId="meta-how-we-work" className="bg-[#F8F9FA] border-y border-gray-100">
        <SectionHeading
          eyebrow="Como trabalhamos"
          title="Um processo em 4 etapas."
          subtitle="Da análise inicial à otimização contínua, cada projeto segue uma metodologia testada e refinada por mais de uma década."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-100 border border-gray-100">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-white p-8 relative" data-testid={`step-${i + 1}`}>
                <div className="absolute top-0 left-0 w-12 h-[3px]" style={{ background: s.color }} />
                <p className="big-number text-3xl text-[#000000]/30">0{i + 1}</p>
                <Icon size={24} className="mt-4" style={{ color: s.color }} />
                <h3 className="font-display text-xl font-bold text-[#000000] mt-4">
                  {s.label}
                </h3>
                <p className="mt-2 text-sm text-[#000000]/70 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      <CTABand
        title="Vamos gerar mais clientes nas redes sociais?"
        subtitle="Fale com nossa equipe e descubra como aumentar suas vendas através do Facebook e Instagram."
        whatsappMsg="Olá! Quero saber mais sobre as campanhas de Meta Ads."
      />
    </div>
  );
}
